/**
 * usePlatformWebhooks — the /config/webhooks console (plan §14): catalog of the
 * events the platform emits to plugins, the subscriptions, the delivery log,
 * and "send a test event now". Full-only on the backend; the page just renders.
 */
import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { log, error as logError } from '../utils/DebugLogger'
import '../contracts/PlatformWebhooks_CatalogResponse'
import '../contracts/PlatformWebhooks_SubscriptionsResponse'
import '../contracts/PlatformWebhooks_DeliveriesResponse'
import '../contracts/PlatformWebhooks_TestSendResponse'
import '../contracts/PlatformWebhooks_SubscriptionSaveResponse'
import '../contracts/PlatformWebhooks_DeleteResponse'
import '../contracts/PlatformWebhooks_TestSendInput'
import '../contracts/PlatformWebhooks_RetryInput'
import '../contracts/PlatformWebhooks_SubscriptionSaveInput'
import '../contracts/PlatformWebhooks_SubscriptionDeleteInput'
import '../contracts/PlatformWebhooks_DeliveriesQueryInput'
import type { PlatformWebhooks_EventRow } from '../contracts/PlatformWebhooks_EventRow'
import type { PlatformWebhooks_SubscriptionRow } from '../contracts/PlatformWebhooks_SubscriptionRow'
import type { PlatformWebhooks_DeliveryRow } from '../contracts/PlatformWebhooks_DeliveryRow'
import type { PlatformWebhooks_TestSendInput } from '../contracts/PlatformWebhooks_TestSendInput'
import type { PlatformWebhooks_SubscriptionSaveInput } from '../contracts/PlatformWebhooks_SubscriptionSaveInput'

// Caminhos diretos do controller (config/platform_webhooks) — o X-ray do harness resolve
// controller/método por aqui; as rotas amigáveis /platform-webhooks/* existem para o MCP e curl.
const BASE = '/index.php/config/platform_webhooks'

/** The envelope's own failure ({success:false, error}) is an error for the page, not an empty list. */
const unwrap = <T>(r: { success?: boolean; dados?: T; error?: string | null }): T => {
  if (r.success === false) throw new Error(r.error || 'Falha na plataforma')
  return r.dados as T
}

export function usePlatformWebhooks() {
  const catalog = ref<PlatformWebhooks_EventRow[]>([])
  const subscriptions = ref<PlatformWebhooks_SubscriptionRow[]>([])
  const deliveries = ref<PlatformWebhooks_DeliveryRow[]>([])
  const loading = ref(false)
  const lastError = ref<string | null>(null)
  const mintedSecret = ref<string | null>(null)

  const run = async <T>(label: string, fn: () => Promise<T>): Promise<T | null> => {
    loading.value = true
    lastError.value = null
    try {
      return await fn()
    } catch (e) {
      lastError.value = e instanceof Error ? e.message : String(e)
      logError('usePlatformWebhooks', label, lastError.value)
      return null
    } finally {
      loading.value = false
    }
  }

  const fetchCatalog = () => run('fetchCatalog', async () => {
    const r = await httpService.get(`${BASE}/catalog`, { responseContract: 'PlatformWebhooks_CatalogResponse' })
    catalog.value = unwrap<{ rows: PlatformWebhooks_EventRow[] }>(r)?.rows ?? []
    log('usePlatformWebhooks', 'fetchCatalog', `${catalog.value.length} eventos`)
    return catalog.value
  })

  const fetchSubscriptions = () => run('fetchSubscriptions', async () => {
    const r = await httpService.get(`${BASE}/subscriptions`, { responseContract: 'PlatformWebhooks_SubscriptionsResponse' })
    subscriptions.value = unwrap<{ rows: PlatformWebhooks_SubscriptionRow[] }>(r)?.rows ?? []
    return subscriptions.value
  })

  const fetchDeliveries = (subscriptionId?: number) => run('fetchDeliveries', async () => {
    const url = subscriptionId ? `${BASE}/deliveries?subscription_id=${subscriptionId}` : `${BASE}/deliveries`
    const r = await httpService.get(url, { inputContract: 'PlatformWebhooks_DeliveriesQueryInput', responseContract: 'PlatformWebhooks_DeliveriesResponse' })
    deliveries.value = unwrap<{ rows: PlatformWebhooks_DeliveryRow[] }>(r)?.rows ?? []
    return deliveries.value
  })

  const testSend = (input: PlatformWebhooks_TestSendInput) => run('testSend', async () => {
    const r = await httpService.post(`${BASE}/test_send`, input, { inputContract: 'PlatformWebhooks_TestSendInput', responseContract: 'PlatformWebhooks_TestSendResponse' })
    const dados = unwrap<{ delivered: boolean; delivery: PlatformWebhooks_DeliveryRow | null }>(r)
    await fetchDeliveries(input.subscription_id)
    return dados ?? null
  })

  const retry = (deliveryId: number) => run('retry', async () => {
    const r = await httpService.post(`${BASE}/retry`, { delivery_id: deliveryId }, { inputContract: 'PlatformWebhooks_RetryInput', responseContract: 'PlatformWebhooks_TestSendResponse' })
    const dados = unwrap<{ delivered: boolean; delivery: PlatformWebhooks_DeliveryRow | null }>(r)
    await fetchDeliveries()
    return dados ?? null
  })

  const saveSubscription = (input: PlatformWebhooks_SubscriptionSaveInput) => run('saveSubscription', async () => {
    const r = await httpService.post(`${BASE}/subscription_save`, input, { inputContract: 'PlatformWebhooks_SubscriptionSaveInput', responseContract: 'PlatformWebhooks_SubscriptionSaveResponse' })
    const dados = unwrap<{ row: PlatformWebhooks_SubscriptionRow | null; created: boolean; secret: string | null }>(r)
    mintedSecret.value = dados?.secret ?? null
    await fetchSubscriptions()
    return dados ?? null
  })

  const deleteSubscription = (id: number) => run('deleteSubscription', async () => {
    const r = await httpService.post(`${BASE}/subscription_delete`, { id }, { inputContract: 'PlatformWebhooks_SubscriptionDeleteInput', responseContract: 'PlatformWebhooks_DeleteResponse' })
    const dados = unwrap<{ deleted: boolean }>(r)
    await fetchSubscriptions()
    return dados?.deleted ?? false
  })

  return { catalog, subscriptions, deliveries, loading, lastError, mintedSecret, fetchCatalog, fetchSubscriptions, fetchDeliveries, testSend, retry, saveSubscription, deleteSubscription }
}
